import { useEffect, useState, type ReactNode } from "react";

/**
 * Renders children only after the component has mounted in the browser.
 *
 * Why: during static-site generation (SSG) the React app runs in Node, where
 * `window`, `document`, `Canvas`, `IntersectionObserver`, `requestAnimationFrame`
 * etc. don't exist. Components that depend on those APIs would crash the SSG
 * build or paint nothing useful into the static HTML.
 *
 * `ClientOnly` solves this by rendering `null` on the server and on the very
 * first client render (so the hydrated DOM matches the SSG HTML — no React
 * hydration warning), then flipping to render its children once `useEffect`
 * fires post-hydration.
 *
 * Use it for: Starfield, FlightHUD, anything driving Canvas / scroll listeners
 * / IntersectionObserver / RAF loops.
 */
export function ClientOnly({ children }: { children: ReactNode }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  return mounted ? <>{children}</> : null;
}
